import { ReactNode, useCallback, useMemo } from "react";
import classNames from "classnames";
import { noop } from "@common/helpers/function";
import { ScheduleTemplateStatus } from "@common/types/domains/accounting/schedule";
import { SCHEDULE_POLICY_KIND_LABELS } from "@common/helpers/domains/accounting/schedulePolicyKind";
import {
    DollarCircleIcon,
    FolderIcon,
    ReceiptIcon,
    StatusIcon,
    TemplatesIcon,
    TriangleIcon,
} from "@icons";
import { AggregatedFilters } from "@components/pageHeader/filters/AggregatedFilters/AggregatedFilters";
import { AggregatedFiltersOverlay } from "@components/pageHeader/filters/AggregatedFilters/AggregatedFiltersOverlay";
import { AggregatedFiltersProvider } from "@components/pageHeader/filters/AggregatedFilters/AggregatedFiltersProvider";
import { useAggregatedFiltersContext } from "@components/pageHeader/filters/AggregatedFilters/AggregatedFilters.context";
import { ActiveFilterButton } from "@components/pageHeader/filters/ActiveFilters/ActiveFilterButton";
import { ActiveFilters } from "@components/pageHeader/filters/ActiveFilters/ActiveFilters";
import { formatFilterLabelText } from "@components/pageHeader/filters/ActiveFilters/formatFilterLabel";
import { FilterNavTab } from "@components/pageHeader/filters/FilterNavTab";
import { FilterTabPanel } from "@components/pageHeader/filters/FilterTabPanel";
import {
    FilterMultiselect,
    FilterMultiselectOption,
    FilterMultiselectValue,
} from "@components/pageHeader/filters/FilterMultiselect/FilterMultiselect";
import overlayStyles from "@components/pageHeader/filters/FiltersOverlay.module.scss";
import statusStyles from "@components/pageHeader/filters/Tabs/TransactionStatusFilter/TransactionStatusFilter.module.scss";
import {
    PolicyFilterOption,
    resolvePolicyFilterLabel,
} from "./policyFilterOptions";
import {
    ScheduleTemplateFilters,
    ScheduleTemplateJournalEntriesFilter,
} from "./scheduleListHelpers";
import { isStringFilterValue } from "@components/pageHeader/filters/FilterMultiselect/isStringFilterValue";
import {
    getPolicyKindFilterOptions,
    isSchedulePolicyKind,
} from "./policyKindFilterOptions";
import { useScheduleHubScopes } from "./ScheduleHubScopeProvider";

const POLICY_TAB = "policy";
const STATUS_TAB = "status";
const JOURNAL_ENTRIES_TAB = "journalEntries";
const TYPE_TAB = "type";
const GROUP_TAB = "group";

type SchedulePoliciesFilterTab =
    | typeof POLICY_TAB
    | typeof STATUS_TAB
    | typeof JOURNAL_ENTRIES_TAB
    | typeof TYPE_TAB
    | typeof GROUP_TAB;

const STATUS_ORDER: ScheduleTemplateStatus[] = [
    ScheduleTemplateStatus.active,
    ScheduleTemplateStatus.archived,
];

export const SCHEDULE_TEMPLATE_STATUS_LABELS: Record<
    ScheduleTemplateStatus,
    string
> = {
    [ScheduleTemplateStatus.active]: "Active",
    [ScheduleTemplateStatus.archived]: "Archived",
    [ScheduleTemplateStatus.deleted]: "Deleted",
};

const JOURNAL_ENTRIES_ORDER: ScheduleTemplateJournalEntriesFilter[] = [
    "with",
    "without",
];

const JOURNAL_ENTRIES_LABELS: Record<
    ScheduleTemplateJournalEntriesFilter,
    string
> = {
    with: "Has journal entries",
    without: "No journal entries",
};

const STATUS_OPTIONS: FilterMultiselectOption[] = STATUS_ORDER.map(
    (status) => ({
        value: status,
        label: SCHEDULE_TEMPLATE_STATUS_LABELS[status],
    }),
);

const JOURNAL_ENTRIES_OPTIONS: FilterMultiselectOption[] =
    JOURNAL_ENTRIES_ORDER.map((option) => ({
        value: option,
        label: JOURNAL_ENTRIES_LABELS[option],
    }));

function isTemplateStatus(
    value: FilterMultiselectValue,
): value is ScheduleTemplateStatus {
    return (
        typeof value === "string" &&
        STATUS_ORDER.some((status) => status === value)
    );
}

function isJournalEntriesFilter(
    value: FilterMultiselectValue,
): value is ScheduleTemplateJournalEntriesFilter {
    return (
        typeof value === "string" &&
        JOURNAL_ENTRIES_ORDER.some((option) => option === value)
    );
}

const SchedulePoliciesFilterNavTab = ({
    tabKey,
    label,
    icon,
}: Readonly<{
    tabKey: SchedulePoliciesFilterTab;
    label: string;
    icon: ReactNode;
}>) => (
    <FilterNavTab eventKey={tabKey} className={overlayStyles.navigationButton}>
        <span className={overlayStyles.navigationItem}>
            <span className={overlayStyles.navigationIcon}>{icon}</span>
            {label}
            <TriangleIcon
                className={classNames(
                    overlayStyles.navigationActiveIndicator,
                    "icon-color-gray-500",
                )}
            />
        </span>
    </FilterNavTab>
);

const SchedulePoliciesFilterPanel = ({
    tabKey,
    name,
    options,
    value,
    onChange,
    isSearchable,
}: Readonly<{
    tabKey: SchedulePoliciesFilterTab;
    name: string;
    options: FilterMultiselectOption[];
    value: FilterMultiselectValue[];
    onChange: (values: FilterMultiselectValue[]) => void;
    isSearchable?: boolean;
}>) => (
    <FilterTabPanel eventKey={tabKey}>
        <div className={statusStyles.filter}>
            <FilterMultiselect
                inDropdown={false}
                searchable={isSearchable}
                options={options}
                value={value}
                onChange={onChange}
                name={name}
                onBlur={noop}
            />
        </div>
    </FilterTabPanel>
);

const SchedulePoliciesActiveFilterChip = ({
    tabKey,
    labels,
    pluralNoun,
    onReset,
}: Readonly<{
    tabKey: SchedulePoliciesFilterTab;
    labels: string[];
    pluralNoun: string;
    onReset: () => void;
}>) => {
    const { open } = useAggregatedFiltersContext();
    const text = formatFilterLabelText(labels, pluralNoun);

    if (text === null) {
        return null;
    }

    return (
        <ActiveFilterButton
            openFilter={() => open(tabKey)}
            resetFilter={onReset}
            label={text}
        />
    );
};

interface SchedulePoliciesFiltersControlsProps {
    filters: ScheduleTemplateFilters;
    onFiltersChange: (filters: ScheduleTemplateFilters) => void;
    groupNames: string[];
    policies: PolicyFilterOption[];
}

/**
 * "Add filter" popover + active-filter chips for the Policies tab. Built on
 * the shared `AggregatedFilters` primitives because the shared `FilterTabs`
 * enum has no schedule-policy concepts. The entity filter lives outside, in
 * the header's `EntityFilterCombobox`.
 */
export const SchedulePoliciesFiltersControls = ({
    filters,
    onFiltersChange,
    groupNames,
    policies,
}: SchedulePoliciesFiltersControlsProps) => {
    const activeFilters = useMemo<SchedulePoliciesFilterTab[]>(() => {
        const active: SchedulePoliciesFilterTab[] = [];
        if (filters.templateIds.length > 0) {
            active.push(POLICY_TAB);
        }
        if (filters.statuses.length > 0) {
            active.push(STATUS_TAB);
        }
        if (filters.journalEntries.length > 0) {
            active.push(JOURNAL_ENTRIES_TAB);
        }
        if (filters.policyKinds.length > 0) {
            active.push(TYPE_TAB);
        }
        if (filters.groups.length > 0) {
            active.push(GROUP_TAB);
        }
        return active;
    }, [filters]);
    const scheduleHubScopes = useScheduleHubScopes();
    const policyKindOptions = useMemo(
        () =>
            getPolicyKindFilterOptions(
                scheduleHubScopes.flatMap((scope) => scope.policyKinds),
            ),
        [scheduleHubScopes],
    );
    const groupOptions = useMemo<FilterMultiselectOption[]>(
        () => groupNames.map((name) => ({ value: name, label: name })),
        [groupNames],
    );

    const policyOptions = useMemo<FilterMultiselectOption[]>(
        () => policies.map(({ id, name }) => ({ value: id, label: name })),
        [policies],
    );
    const handlePoliciesChange = useCallback(
        (values: FilterMultiselectValue[]) =>
            onFiltersChange({
                ...filters,
                templateIds: values.filter(isStringFilterValue),
            }),
        [filters, onFiltersChange],
    );
    const handleStatusesChange = useCallback(
        (values: FilterMultiselectValue[]) =>
            onFiltersChange({
                ...filters,
                statuses: values.filter(isTemplateStatus),
            }),
        [filters, onFiltersChange],
    );
    const handleJournalEntriesChange = useCallback(
        (values: FilterMultiselectValue[]) =>
            onFiltersChange({
                ...filters,
                journalEntries: values.filter(isJournalEntriesFilter),
            }),
        [filters, onFiltersChange],
    );
    const handlePolicyKindsChange = useCallback(
        (values: FilterMultiselectValue[]) =>
            onFiltersChange({
                ...filters,
                policyKinds: values.filter(isSchedulePolicyKind),
            }),
        [filters, onFiltersChange],
    );
    const handleGroupsChange = useCallback(
        (values: FilterMultiselectValue[]) =>
            onFiltersChange({
                ...filters,
                groups: values.filter(isStringFilterValue),
            }),
        [filters, onFiltersChange],
    );
    const clearFilters = useCallback(
        () =>
            onFiltersChange({
                ...filters,
                templateIds: [],
                statuses: [],
                journalEntries: [],
                policyKinds: [],
                groups: [],
            }),
        [filters, onFiltersChange],
    );

    return (
        <AggregatedFiltersProvider defaultTab={STATUS_TAB}>
            <AggregatedFilters label="Add filter">
                <AggregatedFiltersOverlay
                    filterClassname={overlayStyles.filter}
                    navigation={
                        <>
                            <SchedulePoliciesFilterNavTab
                                tabKey={POLICY_TAB}
                                label="Policy"
                                icon={<TemplatesIcon />}
                            />
                            <SchedulePoliciesFilterNavTab
                                tabKey={STATUS_TAB}
                                label="Status"
                                icon={<StatusIcon />}
                            />
                            <SchedulePoliciesFilterNavTab
                                tabKey={JOURNAL_ENTRIES_TAB}
                                label="Journal entries"
                                icon={<ReceiptIcon />}
                            />
                            <SchedulePoliciesFilterNavTab
                                tabKey={TYPE_TAB}
                                label="Type"
                                icon={<DollarCircleIcon />}
                            />
                            <SchedulePoliciesFilterNavTab
                                tabKey={GROUP_TAB}
                                label="Group"
                                icon={<FolderIcon />}
                            />
                        </>
                    }
                    content={
                        <>
                            <SchedulePoliciesFilterPanel
                                tabKey={POLICY_TAB}
                                name="Policy"
                                options={policyOptions}
                                value={filters.templateIds}
                                onChange={handlePoliciesChange}
                                isSearchable
                            />
                            <SchedulePoliciesFilterPanel
                                tabKey={STATUS_TAB}
                                name="Status"
                                options={STATUS_OPTIONS}
                                value={filters.statuses}
                                onChange={handleStatusesChange}
                            />
                            <SchedulePoliciesFilterPanel
                                tabKey={JOURNAL_ENTRIES_TAB}
                                name="Journal entries"
                                options={JOURNAL_ENTRIES_OPTIONS}
                                value={filters.journalEntries}
                                onChange={handleJournalEntriesChange}
                            />
                            <SchedulePoliciesFilterPanel
                                tabKey={TYPE_TAB}
                                name="Type"
                                options={policyKindOptions}
                                value={filters.policyKinds}
                                onChange={handlePolicyKindsChange}
                            />
                            <SchedulePoliciesFilterPanel
                                tabKey={GROUP_TAB}
                                name="Group"
                                options={groupOptions}
                                value={filters.groups}
                                onChange={handleGroupsChange}
                                isSearchable
                            />
                        </>
                    }
                />
            </AggregatedFilters>

            {activeFilters.length > 0 && (
                <ActiveFilters
                    activeFilters={activeFilters}
                    maxVisibleFilters={activeFilters.length}
                    clearFilters={clearFilters}
                    renderVisibleFilters={(visibleFilters) =>
                        visibleFilters.map((tabKey) => {
                            if (tabKey === POLICY_TAB) {
                                return (
                                    <SchedulePoliciesActiveFilterChip
                                        key={tabKey}
                                        tabKey={POLICY_TAB}
                                        pluralNoun="Policies"
                                        labels={filters.templateIds.map(
                                            (templateId) =>
                                                resolvePolicyFilterLabel({
                                                    templateId,
                                                    policies,
                                                }),
                                        )}
                                        onReset={() => handlePoliciesChange([])}
                                    />
                                );
                            }
                            if (tabKey === STATUS_TAB) {
                                return (
                                    <SchedulePoliciesActiveFilterChip
                                        key={tabKey}
                                        tabKey={STATUS_TAB}
                                        pluralNoun="Statuses"
                                        labels={filters.statuses.map(
                                            (status) =>
                                                SCHEDULE_TEMPLATE_STATUS_LABELS[
                                                    status
                                                ],
                                        )}
                                        onReset={() => handleStatusesChange([])}
                                    />
                                );
                            }
                            if (tabKey === JOURNAL_ENTRIES_TAB) {
                                return (
                                    <SchedulePoliciesActiveFilterChip
                                        key={tabKey}
                                        tabKey={JOURNAL_ENTRIES_TAB}
                                        pluralNoun="Journal entries"
                                        labels={filters.journalEntries.map(
                                            (option) =>
                                                JOURNAL_ENTRIES_LABELS[option],
                                        )}
                                        onReset={() =>
                                            handleJournalEntriesChange([])
                                        }
                                    />
                                );
                            }
                            if (tabKey === TYPE_TAB) {
                                return (
                                    <SchedulePoliciesActiveFilterChip
                                        key={tabKey}
                                        tabKey={TYPE_TAB}
                                        pluralNoun="Types"
                                        labels={filters.policyKinds.map(
                                            (kind) =>
                                                SCHEDULE_POLICY_KIND_LABELS[
                                                    kind
                                                ],
                                        )}
                                        onReset={() =>
                                            handlePolicyKindsChange([])
                                        }
                                    />
                                );
                            }
                            return (
                                <SchedulePoliciesActiveFilterChip
                                    key={tabKey}
                                    tabKey={GROUP_TAB}
                                    pluralNoun="Groups"
                                    labels={filters.groups}
                                    onReset={() => handleGroupsChange([])}
                                />
                            );
                        })
                    }
                />
            )}
        </AggregatedFiltersProvider>
    );
};
